/**
 * Pick a legible foreground for an ARBITRARY background colour.
 *
 * ## Why this exists
 *
 * `apps/app` is tenant-themed: every brand supplies its own `primary_color`,
 * which `settings/mui/brandRamp.ts` turns into `palette.brand[*]`. Any chrome
 * that FILLS itself with that colour — a count badge, a chip, a pill — cannot
 * hardcode its text colour, because the fill spans the whole gamut. White text
 * on a pale-yellow brand is unreadable; black text on a navy brand is
 * unreadable. The foreground has to be computed per tenant.
 *
 * ## Why WCAG relative luminance and not a brightness average
 *
 * The obvious shortcut is `(r + g + b) / 3 > 128 ? black : white`. It is wrong
 * for saturated colours, because the eye is far more sensitive to green than to
 * blue — the weights are 0.7152 vs 0.0722, a 10x difference the average throws
 * away. Two concrete failures of the average:
 *
 *   - `#0000FF` (pure blue): average 85 -> "dark" -> white text. Correct, but by
 *     luck; its true luminance is 0.0722 while the average implies ~0.33.
 *   - `#00FF00` (pure green): average 85 -> "dark" -> WHITE text. Actually
 *     wrong: green's luminance is 0.7152, so white gives 1.37:1 (illegible)
 *     while black gives 15.3:1.
 *
 * So the average and the correct answer disagree on exactly the saturated hues
 * a brand is most likely to pick. This module implements the real formula
 * (WCAG 2.x, sRGB -> linearise -> weighted sum) and returns the foreground with
 * the higher contrast RATIO, which is the same quantity an accessibility audit
 * measures.
 *
 * ## Scope
 *
 * Parses hex (3/6/8 digit) and `rgb()`/`rgba()` in both comma and space
 * syntax. It does NOT parse `hsl()` or CSS named colours — the tenant colour
 * arrives as hex (`brandRamp` itself only parses hex), and the theme's own
 * fallbacks are hex. Anything unparseable takes `fallback` (see below) rather
 * than throwing, because a badge with a slightly-suboptimal text colour is a
 * far better failure than a component that crashes the page.
 */

export type Rgb = [number, number, number]

const clamp255 = (n: number) => Math.min(255, Math.max(0, n))

/**
 * Parse a CSS colour string to 8-bit RGB. Returns `null` when the syntax is
 * not one this module handles — callers decide what to do about it.
 */
export const parseColor = (input: string | null | undefined): Rgb | null => {
  if (typeof input !== 'string') return null
  const value = input.trim()

  const hex = /^#?([0-9a-f]{3,8})$/i.exec(value)
  if (hex) {
    let h = hex[1]
    // 4- and 8-digit forms carry alpha; luminance ignores it (a badge fill is
    // painted over an opaque surface, so its own alpha is not our concern here).
    if (h.length === 4) h = h.slice(0, 3)
    if (h.length === 8) h = h.slice(0, 6)
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
    if (h.length !== 6) return null
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ]
  }

  // `rgb(0, 0, 0)`, `rgba(0,0,0,.5)`, and the modern `rgb(0 0 0 / 50%)`.
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(value)
  if (rgb) {
    const parts = rgb[1]
      .replace(/\//g, ' ')
      .split(/[\s,]+/)
      .filter(Boolean)
    if (parts.length < 3) return null
    const channels = parts.slice(0, 3).map(p => {
      // Percentages are legal in rgb() and mean 0-100 of 255.
      if (p.endsWith('%')) return clamp255((parseFloat(p) / 100) * 255)
      return clamp255(parseFloat(p))
    })
    if (channels.some(c => !Number.isFinite(c))) return null
    return channels as Rgb
  }

  return null
}

/**
 * WCAG 2.x relative luminance: sRGB channels are linearised, then summed with
 * the perceptual weights. Range 0 (black) to 1 (white).
 *
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export const relativeLuminance = (rgb: Rgb): number => {
  const [r, g, b] = rgb.map(channel => {
    const c = clamp255(channel) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * WCAG contrast ratio between two colours, 1 (identical) to 21 (black/white).
 * Returns `null` if either colour is unparseable, so a caller can tell "no
 * answer" apart from "a bad answer".
 */
export const contrastRatio = (a: string | Rgb, b: string | Rgb): number | null => {
  const rgbA = Array.isArray(a) ? a : parseColor(a)
  const rgbB = Array.isArray(b) ? b : parseColor(b)
  if (!rgbA || !rgbB) return null
  const lA = relativeLuminance(rgbA)
  const lB = relativeLuminance(rgbB)
  const [light, dark] = lA >= lB ? [lA, lB] : [lB, lA]
  return (light + 0.05) / (dark + 0.05)
}

export interface ReadableTextOptions {
  /** The light foreground candidate. Pass `theme.palette.common.white`. */
  light?: string
  /** The dark foreground candidate. Pass `theme.palette.common.black`. */
  dark?: string
  /**
   * Returned when `background` cannot be parsed. Defaults to the LIGHT
   * candidate: a brand's `primary_color` is overwhelmingly a saturated
   * mid-to-dark colour (and this app's own default primary is dark), so white
   * is the better blind guess. It is a guess, and it is only reached for
   * syntaxes this module does not parse — see the module comment.
   */
  fallback?: 'light' | 'dark'
}

/**
 * The higher-contrast of `light` / `dark` against `background`.
 *
 * The crossover sits at a background luminance of ~0.179, which is where
 * `1.05 / (L + 0.05)` and `(L + 0.05) / 0.05` cross — i.e. the point at which
 * white and black are equally legible (both ~4.58:1).
 */
export const readableTextOn = (
  background: string | Rgb,
  options: ReadableTextOptions = {},
): string => {
  const { light = '#FFFFFF', dark = '#000000', fallback = 'light' } = options
  const bg = Array.isArray(background) ? background : parseColor(background)
  if (!bg) return fallback === 'dark' ? dark : light

  const againstLight = contrastRatio(bg, light)
  const againstDark = contrastRatio(bg, dark)
  if (againstLight === null || againstDark === null) {
    return fallback === 'dark' ? dark : light
  }
  return againstLight >= againstDark ? light : dark
}

/* ------------------------------------------------------------------------- *
 * Theme-derived colours (VRG-827)
 *
 * Everything above answers "given a background, which of black/white is
 * legible on it". These two answer a different question: "given the brand's
 * OWN two colours, what should a muted label and a raised card be".
 *
 * They live here rather than in `settings/mui/palette.ts` because they are
 * pure colour arithmetic with no MUI dependency — which is what lets a guard
 * spec fuzz them over thousands of backgrounds without mounting a theme.
 * ------------------------------------------------------------------------- */

/** WCAG 2.x AA floor for normal-size body text. */
export const WCAG_AA_NORMAL = 4.5

/**
 * How far `mutedTextOn` mixes the body text toward the background before the
 * contrast floor starts clamping it.
 *
 * 0.4525 is not a round number because it is CALIBRATED: on the unthemed pair
 * (`#000000` text, `#F7F7F8` background) it reproduces `#707070` — the exact
 * literal this function replaces — to the byte. The window that rounds to
 * `#707070` is 0.4515..0.4536, so this sits mid-range rather than on an edge
 * where a float change could tip it.
 *
 * Calibrating the constant instead of special-casing the default is what keeps
 * this ONE code path. A branch for "brand has not themed" would be a second
 * path that nothing exercises and therefore nothing protects.
 */
export const MUTED_MIX = 0.4525

/**
 * Luminance step between a page background and a card raised off it.
 *
 * 0.07 is the step the app ALREADY ships: `#F7F7F8` page to `#FFFFFF` card is
 * 0.9307 -> 1.0000. So on the unthemed default `surfaceOn` returns `#FFFFFF`
 * and nothing moves; on a brand background it reproduces the same relationship
 * rather than inventing a new one.
 */
export const SURFACE_LUMINANCE_STEP = 0.07

/**
 * The smallest page/card boundary worth having.
 *
 * `#FFFFFF` page to `#FFFFFF` card is 1.00:1 — the defect `stateCardSurface.ts`
 * measured and fixed ("the card had, in the literal sense, no surface of its
 * own"). The default's real boundary is 1.071:1, so 1.04 sits below what ships
 * and above the degenerate case. It is the test for WHICH DIRECTION to step,
 * not a quality bar.
 */
export const MIN_SURFACE_BOUNDARY = 1.04

const mix = (a: Rgb, b: Rgb, t: number): Rgb =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t] as Rgb

/** Quantise to 8-bit. MUST happen before any contrast check — see `mutedTextOn`. */
const quantise = (rgb: Rgb): Rgb =>
  rgb.map(c => Math.max(0, Math.min(255, Math.round(c)))) as Rgb

const toHex = (rgb: Rgb): string =>
  '#' + quantise(rgb).map(c => c.toString(16).padStart(2, '0')).join('').toUpperCase()

/**
 * Binary-search the mix fraction from `from` toward `anchor` that lands on
 * `targetLuminance`. 40 iterations resolves far finer than 8-bit output.
 */
const towardLuminance = (from: Rgb, anchor: Rgb, targetLuminance: number): Rgb => {
  const ascending = relativeLuminance(anchor) > relativeLuminance(from)
  let lo = 0
  let hi = 1
  for (let i = 0; i < 40; i += 1) {
    const m = (lo + hi) / 2
    if (relativeLuminance(mix(from, anchor, m)) < targetLuminance === ascending) lo = m
    else hi = m
  }
  return quantise(mix(from, anchor, (lo + hi) / 2))
}

/**
 * A de-emphasised text colour derived from the brand's own text and background.
 *
 * ## The defect this replaces
 *
 * `text.secondary` was a hardcoded `#707070`, and its docblock stated it was
 * tuned "on bare white". `text.primary` IS the brand's `text_color`; the muted
 * variant was not derived from the theme at all. Measured against a dark brand
 * background it collapses:
 *
 * | ground | #707070 |
 * |---|---|
 * | `#FFFFFF` — what it was tuned on | 4.95:1 |
 * | `#F7F7F8` — unthemed app background | 4.63:1 |
 * | `#6E5356` — a dark brand background | **1.40:1** |
 * | `#2A2A2E` | 2.89:1 |
 *
 * That docblock also claimed "darkening text is monotone — every surface gains
 * contrast and none can lose it". That holds only on a LIGHT ground. Against a
 * dark background darkening moves the text TOWARD it and strictly loses
 * contrast, which is why the value was never revisited and the failure went
 * unnoticed: the invariant was stated confidently and was true of every case
 * anyone had measured.
 *
 * ## Why mix rather than just use `text_color`
 *
 * The obvious fix — render labels in the body text colour — makes the label and
 * the value in `ProductDetailsPanel`'s two-column table the same weight, and the
 * table stops reading as label/value. The recession is intentional, so it is
 * preserved and the FLOOR is enforced separately.
 *
 * ## The rounding trap, which bit during development
 *
 * The search must test the QUANTISED colour. Converging on a continuous mix that
 * clears 4.5:1 and then rounding to 8-bit can push the shipped colour back under
 * — charcoal-on-cream converged at 4.50:1 and rendered 4.48:1. So `ok()` rounds
 * first, and the result walks back until the rounded colour clears the floor.
 *
 * @returns the derived hex, or `text` unchanged when `text` itself already fails
 *          against `bg` (a brand that picked an illegible pair — muting it
 *          further cannot help, and substituting some other colour would
 *          misrepresent their theme rather than fix it).
 */
export const mutedTextOn = (
  text: string | null | undefined,
  bg: string | null | undefined,
  options: { mix?: number; floor?: number } = {},
): string | null => {
  const { mix: preferred = MUTED_MIX, floor = WCAG_AA_NORMAL } = options
  const fg = parseColor(text)
  const ground = parseColor(bg)
  if (!fg || !ground) return null

  const base = contrastRatio(fg, ground)
  if (base === null || base < floor) return toHex(fg)

  const ok = (t: number) => (contrastRatio(quantise(mix(fg, ground, t)), ground) ?? 0) >= floor
  if (ok(preferred)) return toHex(mix(fg, ground, preferred))

  let lo = 0
  let hi = preferred
  for (let i = 0; i < 40; i += 1) {
    const m = (lo + hi) / 2
    if (ok(m)) lo = m
    else hi = m
  }
  // Quantisation can leave `lo` a hair over the edge; step back until the
  // colour that actually ships clears the floor.
  for (let i = 0; i < 64 && lo > 0 && !ok(lo); i += 1) lo = Math.max(0, lo - 0.004)
  return toHex(mix(fg, ground, Math.max(lo, 0)))
}

/**
 * A raised card surface derived from the brand's page background.
 *
 * ## The defect this replaces
 *
 * `palette.ts` set only `background.default`. `background.paper` was never
 * themed, so MUI's default `#fff` won — and every consumer of it, including
 * `stateCardSurfaceSx` (spread across the certificate page's state cards AND
 * the Gift / Sell / Report tiles), rendered a white card on a brand's dark
 * background, live, for every brand.
 *
 * ## Why not simply `background.paper = bg_color`
 *
 * That is the obvious reading of "make the cards the background colour", and it
 * re-creates a bug this codebase has already fixed once: card equal to page is a
 * 1.00:1 boundary with nothing telling the eye where the block begins. See
 * `stateCardSurface.ts`, which measured exactly that.
 *
 * ## Direction is decided by the ACHIEVED boundary, not the reachable target
 *
 * Lighter first, because that is the relationship the app already ships (grey
 * page, white card) and it is also the standard elevation convention on dark
 * grounds. Darker only when the background is so close to white that lightening
 * cannot produce a perceptible step.
 *
 * Testing reachability instead (`bgL + step <= 1`) is subtly wrong and was the
 * first implementation: `#F7F7F8` has luminance 0.9307, so `0.9307 + 0.07` is
 * 1.0007 — marginally unreachable — and the default page flipped to a DARKER
 * card, inverting the app's own light-mode relationship on a floating-point
 * hair. Clamping the target to 1.0 and judging the result instead gives
 * `#FFFFFF` for the default and `#F7F7F7` for a pure-white brand background.
 */
export const surfaceOn = (
  bg: string | null | undefined,
  options: { step?: number; minBoundary?: number } = {},
): string | null => {
  const { step = SURFACE_LUMINANCE_STEP, minBoundary = MIN_SURFACE_BOUNDARY } = options
  const ground = parseColor(bg)
  if (!ground) return null

  const bl = relativeLuminance(ground)
  const lighter = towardLuminance(ground, [255, 255, 255], Math.min(1, bl + step))
  if ((contrastRatio(ground, lighter) ?? 1) >= minBoundary) return toHex(lighter)
  return toHex(towardLuminance(ground, [0, 0, 0], Math.max(0, bl - step)))
}
